/**
 * The admin panel's single data layer.
 *
 * Every screen reads through this module and none of them know whether the
 * figures came from Postgres or from the demo generator. That is the point: the
 * pages are written once, and `isDemo` decides at the boundary.
 *
 * Writes behave differently from reads in demo mode. A read returns simulated
 * data; a write **refuses**, loudly, via DemoWriteError. Silently pretending to
 * save would teach the operator that a button works when it does not, and the
 * first time they relied on it for real they would lose the edit.
 */

import { supabase, isDemo } from './supabase'
import * as demo from './demo'

export { isDemo }

export class DemoWriteError extends Error {
  constructor(action = 'That change') {
    super(`${action} is disabled in demo mode. Connect Supabase to make real changes.`)
    this.name = 'DemoWriteError'
  }
}

const NOT_ALLOWED = "your role doesn't allow this. Ask the owner or a manager."

/** Unwraps a PostgREST response, turning its error into a throw. */
function unwrap({ data, error }, context) {
  if (error) {
    const err = new Error(`${context}: ${error.message}`)
    err.cause = error
    // 42501 is insufficient_privilege: a row-level-security refusal on insert,
    // or a function this account may not call.
    if (error.code === '42501') {
      err.message = `${context}: ${NOT_ALLOWED}`
    }
    // PGRST116: .single() found no row. On an update that means row level
    // security filtered it out (or it has just been deleted) — say so instead
    // of "JSON object requested, multiple (or no) rows returned".
    if (error.code === 'PGRST116') {
      err.message = `${context}: nothing was changed — it may have been removed, or ${NOT_ALLOWED}`
    }
    throw err
  }
  return data
}

/**
 * For deletes. Row level security does not error on a refused delete — it
 * quietly deletes nothing — so without this a staff member pressing Delete was
 * told it worked. Call with `.select('id')` on the delete.
 */
function unwrapDeleted(response, context) {
  const rows = unwrap(response, context)
  if (!rows || rows.length === 0) {
    throw new Error(`${context}: nothing was deleted — it may already be gone, or ${NOT_ALLOWED}`)
  }
  return rows
}

// ===========================================================================
//  Dashboard
// ===========================================================================

export async function getDashboardSummary() {
  if (isDemo) return demo.demoDashboardSummary()
  // A view, so this is one round trip rather than a dozen count queries.
  return unwrap(
    await supabase.from('admin_dashboard_summary').select('*').single(),
    'Loading dashboard summary',
  )
}

export async function getDailySales({ days = 30 } = {}) {
  if (isDemo) return demo.demoDailySales({ days })

  const from = new Date()
  from.setDate(from.getDate() - (days - 1))
  const rows = unwrap(
    await supabase
      .from('admin_daily_sales')
      .select('*')
      .gte('day', from.toISOString().slice(0, 10))
      .order('day', { ascending: true }),
    'Loading daily sales',
  )

  // The view only has rows for days that traded. Charting it raw would draw a
  // line straight across a quiet week and compress the x-axis unevenly, so the
  // gaps are filled with explicit zeros.
  const byDay = new Map((rows ?? []).map((r) => [r.day, r]))
  const filled = []
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date()
    d.setDate(d.getDate() - i)
    const key = d.toISOString().slice(0, 10)
    const row = byDay.get(key)
    filled.push({
      day: key,
      revenue: Number(row?.revenue ?? 0),
      order_count: Number(row?.order_count ?? 0),
      units: Number(row?.units ?? 0),
    })
  }
  return filled
}

export async function getProductPerformance() {
  if (isDemo) return demo.demoProductPerformance()
  return unwrap(
    await supabase.from('admin_product_performance').select('*').order('revenue', { ascending: false }),
    'Loading product performance',
  )
}

export async function getTrending() {
  if (isDemo) return demo.demoTrending()
  return unwrap(
    await supabase.from('admin_trending_products').select('*').order('units_7d', { ascending: false }),
    'Loading trending products',
  )
}

export async function getCategoryPerformance() {
  if (isDemo) return demo.demoCategoryPerformance()
  return unwrap(
    await supabase.from('admin_category_performance').select('*').order('revenue', { ascending: false }),
    'Loading category performance',
  )
}

export async function getLowStock() {
  if (isDemo) return demo.demoLowStock()
  return unwrap(
    await supabase.from('admin_low_stock').select('*').order('available', { ascending: true }),
    'Loading low stock',
  )
}

export async function getOrderStatusCounts() {
  if (isDemo) return demo.demoStatusCounts()
  const rows = unwrap(await supabase.from('orders').select('status'), 'Loading order statuses')
  const counts = new Map()
  for (const r of rows ?? []) counts.set(r.status, (counts.get(r.status) ?? 0) + 1)
  return counts
}

export async function getActivity({ limit = 12 } = {}) {
  if (isDemo) return demo.demoActivity()
  return unwrap(
    await supabase.from('activity_log').select('*').order('created_at', { ascending: false }).limit(limit),
    'Loading activity',
  )
}

// ===========================================================================
//  Products
// ===========================================================================

export async function getProducts() {
  if (isDemo) return demo.demoProducts()

  const [products, perf] = await Promise.all([
    unwrap(
      await supabase
        .from('products')
        .select('*, categories:primary_category_id (id, name)')
        .order('sort_order', { ascending: true }),
      'Loading products',
    ),
    getProductPerformance().catch(() => []),
  ])

  const byId = new Map((perf ?? []).map((p) => [p.id, p]))
  return (products ?? []).map((p) => ({
    ...p,
    category: p.categories?.name ?? null,
    units_sold: byId.get(p.id)?.units_sold ?? 0,
    revenue: byId.get(p.id)?.revenue ?? 0,
    views: byId.get(p.id)?.views ?? 0,
    stock_on_hand: byId.get(p.id)?.stock_on_hand ?? 0,
  }))
}

export async function getProduct(id) {
  if (isDemo) return demo.demoProducts().find((p) => p.id === id) ?? null
  const row = unwrap(
    await supabase
      .from('products')
      .select('*, categories:primary_category_id (id, name), product_categories (category_id)')
      .eq('id', id)
      .single(),
    'Loading product',
  )
  return { ...row, category: row.categories?.name ?? null }
}

export async function saveProduct(id, patch) {
  if (isDemo) throw new DemoWriteError('Saving a product')

  // public_id and slug are generated by the database. Letting the form post
  // them back would either fail the unique constraint or silently renumber a
  // live product's URL.
  const { public_id, slug, categories, product_categories, units_sold, revenue, views, stock_on_hand, category, ...clean } = patch

  if (id) {
    const row = unwrap(
      await supabase.from('products').update(clean).eq('id', id).select().single(),
      'Saving product',
    )
    await logActivity('product.updated', 'product', id, `Updated ${row.name}`)
    return row
  }

  const row = unwrap(await supabase.from('products').insert(clean).select().single(), 'Creating product')
  await logActivity('product.created', 'product', row.id, `Added ${row.name}`)
  return row
}

export async function setProductStatus(id, status) {
  if (isDemo) throw new DemoWriteError('Changing product status')
  const row = unwrap(
    await supabase.from('products').update({ status }).eq('id', id).select().single(),
    'Updating product status',
  )
  await logActivity('product.status_changed', 'product', id, `${row.name} → ${status}`)
  return row
}

/**
 * What would be lost by deleting this product.
 *
 * Asked BEFORE offering the choice, because the honest answer changes which
 * option should be recommended. A product that has never sold can be removed
 * cleanly; one that appears on invoices cannot, and the operator deserves to
 * know that before clicking rather than after.
 */
export async function getProductUsage(id) {
  if (isDemo) return { orderLines: 0, unitsSold: 0, stockLines: 0 }

  const [lines, stock] = await Promise.all([
    supabase.from('order_items').select('quantity').eq('product_id', id),
    supabase.from('inventory').select('quantity').eq('product_id', id),
  ])

  return {
    orderLines: lines.data?.length ?? 0,
    unitsSold: (lines.data ?? []).reduce((n, r) => n + (r.quantity ?? 0), 0),
    stockLines: stock.data?.length ?? 0,
    stockUnits: (stock.data ?? []).reduce((n, r) => n + (r.quantity ?? 0), 0),
  }
}

/**
 * Hide a product without destroying anything.
 *
 * The right answer almost always. It leaves the shop, stops being orderable, and
 * every past order, sales figure and stock movement stays intact and attributed.
 */
export async function archiveProduct(id) {
  if (isDemo) throw new DemoWriteError('Archiving a product')
  const row = unwrap(
    await supabase.from('products').update({ status: 'archived' }).eq('id', id).select().single(),
    'Archiving product',
  )
  await logActivity('product.archived', 'product', id, `Archived ${row.name}`)
  return row
}

/**
 * Delete a product outright.
 *
 * Irreversible, and it takes the stock lines and category links with it
 * (both cascade). Order lines survive — they carry their own snapshot of the
 * name, sku and price — but their link back to the product becomes null, so the
 * product disappears from sales reporting even though the money was real.
 *
 * That is why the UI checks order history first and pushes archiving instead.
 * Uploaded images are deliberately NOT removed from storage: another product may
 * reference the same file, and an orphaned image costs nothing next to a
 * silently broken one.
 */
export async function deleteProduct(id) {
  if (isDemo) throw new DemoWriteError('Deleting a product')

  // Read the name first — after the delete there is nothing left to name it by
  // in the audit log.
  const { data: product } = await supabase.from('products').select('name, sku').eq('id', id).maybeSingle()

  unwrapDeleted(await supabase.from('products').delete().eq('id', id).select('id'), 'Deleting product')

  await logActivity(
    'product.deleted',
    'product',
    id,
    `Deleted ${product?.name ?? 'a product'}${product?.sku ? ` (${product.sku})` : ''}`,
    { name: product?.name, sku: product?.sku },
  )
}

// ===========================================================================
//  Categories
// ===========================================================================

export async function getCategories() {
  if (isDemo) return demo.demoCategories()
  const rows = unwrap(
    await supabase.from('categories').select('*').order('sort_order', { ascending: true }),
    'Loading categories',
  )
  // A category's product count is what tells the operator whether it is safe to
  // delete, so it is worth the extra query.
  const counts = unwrap(
    await supabase.from('products').select('primary_category_id'),
    'Counting products per category',
  )
  const tally = new Map()
  for (const r of counts ?? []) tally.set(r.primary_category_id, (tally.get(r.primary_category_id) ?? 0) + 1)
  return (rows ?? []).map((c) => ({ ...c, product_count: tally.get(c.id) ?? 0 }))
}

export async function saveCategory(id, patch) {
  if (isDemo) throw new DemoWriteError('Saving a category')
  const { product_count, ...clean } = patch
  if (id) {
    return unwrap(await supabase.from('categories').update(clean).eq('id', id).select().single(), 'Saving category')
  }
  return unwrap(await supabase.from('categories').insert(clean).select().single(), 'Creating category')
}

// ===========================================================================
//  Inventory
// ===========================================================================

export async function getInventory() {
  if (isDemo) return demo.demoInventory()
  const rows = unwrap(
    await supabase
      .from('inventory')
      .select('*, products!inner (id, public_id, name, sku, image, status, categories:primary_category_id (name))')
      .order('updated_at', { ascending: false }),
    'Loading inventory',
  )
  return (rows ?? []).map((r) => ({
    ...r,
    public_id: r.products?.public_id,
    name: r.products?.name,
    sku: r.products?.sku,
    image: r.products?.image ?? null,
    category: r.products?.categories?.name ?? null,
    available: Math.max((r.quantity ?? 0) - (r.reserved ?? 0), 0),
  }))
}

/**
 * Relative change, deliberately — see the adjust_stock() comment in the SQL.
 * Two assistants counting the same shelf at once land on the right total with a
 * delta, and clobber each other with an absolute set.
 */
export async function adjustStock({ productId, size = '', color = '', delta, reason = 'manual_adjustment', note }) {
  if (isDemo) throw new DemoWriteError('Adjusting stock')
  return unwrap(
    await supabase.rpc('adjust_stock', {
      p_product_id: productId,
      p_size: size,
      p_color: color,
      p_delta: delta,
      p_reason: reason,
      p_note: note ?? null,
    }),
    'Adjusting stock',
  )
}

/** Absolute set, for a stocktake where the counted number IS the truth. */
export async function setStock({ productId, size = '', color = '', quantity, note }) {
  if (isDemo) throw new DemoWriteError('Setting stock')
  return unwrap(
    await supabase.rpc('set_stock', {
      p_product_id: productId,
      p_size: size,
      p_color: color,
      p_quantity: quantity,
      p_note: note ?? null,
    }),
    'Setting stock',
  )
}

/** The stock lines for one product, for the grid on its edit page. */
export async function getProductInventory(productId) {
  if (isDemo) return demo.demoInventory().filter((r) => r.product_id === productId)
  return unwrap(
    await supabase
      .from('inventory')
      .select('id, size, color, quantity, reserved')
      .eq('product_id', productId),
    'Loading stock for this product',
  )
}

/**
 * Write a product's whole stock grid in one call.
 *
 * One RPC rather than a write per cell: a 9-size, 4-colour shirt is 36 cells,
 * and 36 round trips is both slow and non-atomic — a dropped connection halfway
 * leaves the product half-counted with nothing to show which half.
 */
export async function saveProductStock(productId, rows, note = null) {
  if (isDemo) throw new DemoWriteError('Saving stock')
  return unwrap(
    await supabase.rpc('set_stock_bulk', {
      p_product_id: productId,
      p_rows: rows,
      p_reason: 'stocktake',
      p_note: note,
    }),
    'Saving stock',
  )
}

/**
 * Create the inventory rows for a product from its sizes × colours.
 *
 * Products imported from the static catalogue have no stock rows at all, which
 * the schema reads as "in stock, not tracked". This is the action that opts one
 * into real tracking, starting everything at zero for a physical count.
 */
export async function startTrackingStock(product) {
  if (isDemo) throw new DemoWriteError('Starting stock tracking')
  const sizes = product.sizes?.length ? product.sizes : ['']
  const colors = product.colors?.length ? product.colors : ['']
  const rows = []
  for (const size of sizes) {
    for (const color of colors) rows.push({ product_id: product.id, size, color, quantity: 0 })
  }
  unwrap(
    await supabase.from('inventory').upsert(rows, { onConflict: 'product_id,size,color' }),
    'Creating stock lines',
  )
  await logActivity('inventory.tracking_started', 'product', product.id, `Started tracking stock for ${product.name}`)
  return rows.length
}

export async function getStockMovements({ productId, limit = 50 } = {}) {
  if (isDemo) return []
  let query = supabase.from('stock_movements').select('*').order('created_at', { ascending: false }).limit(limit)
  if (productId) query = query.eq('product_id', productId)
  return unwrap(await query, 'Loading stock movements')
}

// ===========================================================================
//  Orders
// ===========================================================================

export async function getOrders({ status, search, limit = 200 } = {}) {
  if (isDemo) return demo.demoOrders({ status, search, limit })

  let query = supabase.from('orders').select('*').order('placed_at', { ascending: false }).limit(limit)
  if (status && status !== 'all') query = query.eq('status', status)
  if (search) {
    const q = `%${search}%`
    query = query.or(
      `order_number.ilike.${q},customer_email.ilike.${q},customer_first_name.ilike.${q},customer_last_name.ilike.${q}`,
    )
  }
  return unwrap(await query, 'Loading orders')
}

export async function getOrder(id) {
  if (isDemo) return demo.demoOrder(id)
  const order = unwrap(await supabase.from('orders').select('*').eq('id', id).single(), 'Loading order')
  const items = unwrap(
    await supabase.from('order_items').select('*').eq('order_id', id).order('created_at'),
    'Loading order items',
  )
  return { ...order, items: items ?? [] }
}

/**
 * Goes through the RPC rather than a plain update, because a status change has
 * side effects: cancelling an order has to return its stock to the shelf, and
 * reviving it has to take it off again. Those belong in one transaction with the
 * status write, not in two round trips from a browser that might close between
 * them.
 */
export async function setOrderStatus(id, status, note) {
  if (isDemo) throw new DemoWriteError('Changing order status')
  return unwrap(
    await supabase.rpc('set_order_status', { p_order_id: id, p_status: status, p_note: note ?? null }),
    'Updating order status',
  )
}

export async function updateOrder(id, patch) {
  if (isDemo) throw new DemoWriteError('Editing an order')
  return unwrap(await supabase.from('orders').update(patch).eq('id', id).select().single(), 'Updating order')
}

// ===========================================================================
//  Settings
// ===========================================================================

export async function getSettings() {
  if (isDemo) {
    // Mirrors what the migrations insert, including group_label and input_kind,
    // so the Settings page groups and renders exactly as it will against a real
    // database rather than looking subtly different in demo mode.
    return [
      { key: 'shipping_flat_rate', value: 199, group_label: 'Shop', input_kind: 'number', label: 'Standard shipping (R)', description: 'Flat courier fee charged when the order is below the free-shipping threshold.' },
      { key: 'free_shipping_threshold', value: 1500, group_label: 'Shop', input_kind: 'number', label: 'Free shipping from (R)', description: 'Subtotal at or above which shipping is free. Set to 0 to always charge.' },
      { key: 'low_stock_threshold', value: 3, group_label: 'Shop', input_kind: 'number', label: 'Low stock warning at', description: 'Default units remaining before a size is flagged low on the dashboard.' },
      { key: 'announcement_enabled', value: false, group_label: 'Shop', input_kind: 'boolean', label: 'Show announcement bar', description: 'Shows a site-wide message strip above the header.' },
      { key: 'announcement_text', value: '', group_label: 'Shop', input_kind: 'text', label: 'Announcement text', description: 'Message shown in the announcement bar.' },
      { key: 'store_accepting_orders', value: true, group_label: 'Shop', input_kind: 'boolean', label: 'Accepting online orders', description: 'Turn off to hide checkout while leaving the catalogue browsable.' },
      { key: 'require_account_to_order', value: true, group_label: 'Shop', input_kind: 'boolean', label: 'Require an account to order', description: 'When on, shoppers must register or sign in before checking out.' },

      { key: 'invoice_business_name', value: "Coquis Men's Outfitters", group_label: 'Invoicing', input_kind: 'text', label: 'Registered business name', description: 'Printed as the issuer at the top of every document.' },
      { key: 'invoice_business_vat', value: '4123456789', group_label: 'Invoicing', input_kind: 'text', label: 'VAT registration number', description: 'Legally required on a tax invoice. Leave blank if the business is not VAT registered.' },
      { key: 'invoice_business_reg', value: '2014/118422/07', group_label: 'Invoicing', input_kind: 'text', label: 'Company registration number', description: 'CIPC number, e.g. 2019/123456/07.' },
      { key: 'invoice_business_address', value: '295 Galway Avenue\nGalway Centre, Bronberrick\nCenturion, 0157', group_label: 'Invoicing', input_kind: 'textarea', label: 'Business address', description: 'One line per line, exactly as it should print.' },
      { key: 'invoice_business_phone', value: '012 660 2104', group_label: 'Invoicing', input_kind: 'text', label: 'Telephone', description: 'Printed in the issuer block.' },
      { key: 'invoice_business_email', value: 'info@coquis.co.za', group_label: 'Invoicing', input_kind: 'text', label: 'E-mail', description: 'Where queries about a document should go.' },
      { key: 'invoice_bank_details', value: 'Bank: First National Bank\nAccount name: Coquis Men’s Outfitters\nAccount number: 0000000000\nBranch code: 250655', group_label: 'Invoicing', input_kind: 'textarea', label: 'Banking details', description: 'Printed at the foot of every invoice so it can be paid without asking.' },
      { key: 'invoice_terms', value: 'Payment is due within 30 days of the invoice date. Goods remain the property of Coquis Men’s Outfitters until paid for in full.', group_label: 'Invoicing', input_kind: 'textarea', label: 'Default terms', description: 'Pre-filled on each new document. Can be changed per document.' },
      { key: 'invoice_tax_rate', value: 15, group_label: 'Invoicing', input_kind: 'number', label: 'VAT rate (%)', description: 'The South African standard rate is 15%.' },
      { key: 'invoice_prices_include_tax', value: true, group_label: 'Invoicing', input_kind: 'boolean', label: 'Prices include VAT by default', description: 'On for retail, off for trade.' },
      { key: 'invoice_due_days', value: 30, group_label: 'Invoicing', input_kind: 'number', label: 'Payment terms (days)', description: 'How far ahead the due date is set on a new invoice.' },
    ]
  }
  return unwrap(await supabase.from('store_settings').select('*').order('key'), 'Loading settings')
}

export async function saveSetting(key, value) {
  if (isDemo) throw new DemoWriteError('Changing a setting')
  return unwrap(
    await supabase.from('store_settings').update({ value }).eq('key', key).select().single(),
    'Saving setting',
  )
}

// ===========================================================================
//  Image upload
// ===========================================================================

/**
 * Uploads to the public product-images bucket and returns the public URL.
 *
 * Filenames are rewritten rather than trusted. A shop assistant's phone produces
 * names like "WhatsApp Image 2026-09-10 at 14.32.11.jpeg", which break URLs on
 * the spaces and collide the moment a second photo arrives in the same second.
 */
export async function uploadProductImage(file, { productSku } = {}) {
  if (isDemo) throw new DemoWriteError('Uploading an image')

  const extension = (file.name.split('.').pop() ?? 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '')
  const stamp = new Date().toISOString().replace(/[^0-9]/g, '').slice(0, 14)
  const random = Math.random().toString(36).slice(2, 8)
  const folder = (productSku ?? 'misc').toLowerCase().replace(/[^a-z0-9-]/g, '-')
  const path = `${folder}/${stamp}-${random}.${extension}`

  const { error } = await supabase.storage.from('product-images').upload(path, file, {
    cacheControl: '31536000', // a year; the path is unique so it can never go stale
    upsert: false,
  })
  if (error) throw new Error(`Uploading image: ${error.message}`)

  const { data } = supabase.storage.from('product-images').getPublicUrl(path)
  return data.publicUrl
}

// ===========================================================================
//  Activity log
// ===========================================================================

/**
 * Best-effort. A failure to write the audit line must not roll back or mask the
 * change the operator actually made, so it warns rather than throws.
 */
async function logActivity(action, entityType, entityId, summary, details) {
  if (isDemo) return
  try {
    await supabase.rpc('log_activity', {
      p_action: action,
      p_entity_type: entityType,
      p_entity_id: String(entityId),
      p_summary: summary,
      p_details: details ?? null,
    })
  } catch (err) {
    console.warn('[coquis-admin] Could not write the activity log entry.', err)
  }
}

export { logActivity }

// ===========================================================================
//  Team and permissions
// ===========================================================================
//  Every write here goes through a SQL function that checks the caller's role
//  (migration 18): the owner can do anything; a manager can manage staff and
//  managers but not owners or themselves. Enforced in the database, not by the
//  UI hiding a button — a call from the browser console is refused the same way.
// ===========================================================================

export async function getTeam() {
  if (isDemo) return demo.demoTeam()
  return unwrap(
    await supabase.from('admin_profiles').select('*').order('role').order('email'),
    'Loading the team',
  )
}

export async function getInvitations() {
  if (isDemo) return demo.demoInvitations()
  return unwrap(
    await supabase
      .from('admin_invitations')
      .select('*')
      .is('accepted_at', null)
      .order('created_at', { ascending: false }),
    'Loading invitations',
  )
}

export async function inviteAdmin(email, role = 'staff', note = null) {
  if (isDemo) throw new DemoWriteError('Inviting someone')
  return unwrap(
    await supabase.rpc('invite_admin', { p_email: email, p_role: role, p_note: note }),
    'Inviting an administrator',
  )
}

/**
 * Asks the server to e-mail a pending invitation (functions/api/send-invite.js).
 *
 * Never throws. Resolves to { sent: true }, or { sent: false, reason } when
 * automatic e-mail isn't set up or didn't go through — the Team page then hands
 * the owner a ready-made message to send themselves, so nobody is left waiting
 * on an e-mail that was never going to arrive.
 */
export async function sendInviteEmail(email) {
  if (isDemo) return { sent: false, reason: 'demo' }
  try {
    const { data } = await supabase.auth.getSession()
    const res = await fetch('/api/send-invite', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${data.session?.access_token ?? ''}`,
      },
      body: JSON.stringify({ email }),
    })
    const body = await res.json().catch(() => ({}))
    return body.sent === true ? { sent: true } : { sent: false, reason: body.reason ?? `http-${res.status}` }
  } catch {
    return { sent: false, reason: 'unreachable' }
  }
}

export async function revokeInvitation(id) {
  if (isDemo) throw new DemoWriteError('Withdrawing an invitation')
  return unwrap(await supabase.rpc('revoke_invitation', { p_id: id }), 'Withdrawing the invitation')
}

export async function setAdminRole(id, role) {
  if (isDemo) throw new DemoWriteError('Changing a role')
  return unwrap(await supabase.rpc('set_admin_role', { p_id: id, p_role: role }), 'Changing the role')
}

export async function setAdminActive(id, active) {
  if (isDemo) throw new DemoWriteError('Changing access')
  return unwrap(
    await supabase.rpc('set_admin_active', { p_id: id, p_active: active }),
    'Changing access',
  )
}

// ===========================================================================
//  Customers
// ===========================================================================
//  Shopper accounts, distinct from the staff accounts in admin_profiles.
//  Reads go through admin_customers, a view that joins each account to its
//  order count and lifetime spend.
// ===========================================================================

export async function getCustomers() {
  if (isDemo) return demo.demoCustomers()
  return unwrap(
    await supabase.from('admin_customers').select('*').order('total_spent', { ascending: false }),
    'Loading customers',
  )
}

/**
 * Stop or allow an account placing orders.
 *
 * Blocking does not prevent signing in — a blocked customer can still see their
 * own order history, which is what you want during a payment dispute. Cutting
 * off their record makes the argument harder to settle, not easier.
 */
export async function setCustomerBlocked(id, blocked) {
  if (isDemo) throw new DemoWriteError('Blocking a customer')
  const row = unwrap(
    await supabase.from('customers').update({ is_blocked: blocked }).eq('id', id).select().single(),
    'Updating the customer',
  )
  await logActivity(
    blocked ? 'customer.blocked' : 'customer.unblocked',
    'customer',
    id,
    `${row.email} ${blocked ? 'blocked from ordering' : 'allowed to order again'}`,
  )
  return row
}

export async function saveCustomerNotes(id, notes) {
  if (isDemo) throw new DemoWriteError('Saving a note')
  return unwrap(
    await supabase.from('customers').update({ admin_notes: notes || null }).eq('id', id).select().single(),
    'Saving the note',
  )
}

// ===========================================================================
//  Suppliers
// ===========================================================================
//  The address book that makes the invoice generator quick: pick a name and the
//  VAT number, e-mail, phone and address arrive with it.
// ===========================================================================

export async function getSuppliers({ includeInactive = true } = {}) {
  if (isDemo) return demo.demoSuppliers()
  let query = supabase.from('suppliers').select('*').order('name')
  if (!includeInactive) query = query.eq('is_active', true)
  return unwrap(await query, 'Loading suppliers')
}

export async function getSupplier(id) {
  if (isDemo) return demo.demoSuppliers().find((s) => s.id === id) ?? null
  return unwrap(await supabase.from('suppliers').select('*').eq('id', id).single(), 'Loading supplier')
}

export async function saveSupplier(id, patch) {
  if (isDemo) throw new DemoWriteError('Saving a supplier')

  // Blank boxes are absent details, not empty strings. Storing '' would make
  // `vat_number is null` false and print an empty VAT line on the invoice.
  const clean = Object.fromEntries(
    Object.entries(patch).map(([key, value]) =>
      typeof value === 'string' ? [key, value.trim() || null] : [key, value],
    ),
  )
  clean.name = (patch.name ?? '').trim()
  if (!clean.name) throw new Error('A supplier needs a name.')

  if (id) {
    const row = unwrap(
      await supabase.from('suppliers').update(clean).eq('id', id).select().single(),
      'Saving supplier',
    )
    await logActivity('supplier.updated', 'supplier', id, `Updated ${row.name}`)
    return row
  }

  const row = unwrap(await supabase.from('suppliers').insert(clean).select().single(), 'Adding supplier')
  await logActivity('supplier.created', 'supplier', row.id, `Added supplier ${row.name}`)
  return row
}

/**
 * What a supplier is attached to, asked before offering to delete it.
 *
 * Same reasoning as getProductUsage: a supplier that appears on documents cannot
 * be removed cleanly, because the documents keep their own copy of the details
 * but lose the link that groups them. Deactivating is nearly always what was
 * meant — it takes the name out of every dropdown and changes nothing else.
 */
export async function getSupplierUsage(id) {
  if (isDemo) return { invoices: 0, total: 0 }
  const rows = unwrap(
    await supabase.from('invoices').select('total').eq('supplier_id', id),
    'Checking supplier use',
  )
  return {
    invoices: rows?.length ?? 0,
    total: (rows ?? []).reduce((sum, r) => sum + Number(r.total ?? 0), 0),
  }
}

export async function setSupplierActive(id, active) {
  if (isDemo) throw new DemoWriteError('Changing a supplier')
  const row = unwrap(
    await supabase.from('suppliers').update({ is_active: active }).eq('id', id).select().single(),
    'Updating supplier',
  )
  await logActivity(
    active ? 'supplier.reactivated' : 'supplier.deactivated',
    'supplier',
    id,
    `${row.name} ${active ? 'back in the supplier list' : 'hidden from the supplier list'}`,
  )
  return row
}

export async function deleteSupplier(id) {
  if (isDemo) throw new DemoWriteError('Deleting a supplier')
  const { data: supplier } = await supabase.from('suppliers').select('name').eq('id', id).maybeSingle()
  unwrapDeleted(await supabase.from('suppliers').delete().eq('id', id).select('id'), 'Deleting supplier')
  await logActivity('supplier.deleted', 'supplier', id, `Deleted supplier ${supplier?.name ?? ''}`.trim())
}

// ===========================================================================
//  Invoices
// ===========================================================================
//  Reads go through admin_invoices, which adds the supplier's current name, the
//  outstanding balance and a computed is_overdue. Writes go through the RPCs in
//  migration 17 wherever more than one row moves at a time.
//
//  Nothing here sends subtotal, tax_amount or total. The database computes them
//  on every write, so a figure posted from the browser would be discarded — and
//  sending one anyway would imply the client had a say, which it must not.
// ===========================================================================

const DERIVED = ['subtotal', 'discount_amount', 'tax_amount', 'total', 'item_count', 'number']

export async function getInvoices({ kind, status, search, limit = 300 } = {}) {
  if (isDemo) return demo.demoInvoices({ kind, status, search, limit })

  let query = supabase
    .from('admin_invoices')
    .select('*')
    .order('issue_date', { ascending: false })
    .order('number', { ascending: false })
    .limit(limit)

  if (kind && kind !== 'all') query = query.eq('kind', kind)
  if (status && status !== 'all') query = query.eq('status', status)
  if (search) {
    const q = `%${search}%`
    query = query.or(`number.ilike.${q},bill_to_name.ilike.${q},reference.ilike.${q}`)
  }
  return unwrap(await query, 'Loading invoices')
}

export async function getInvoice(id) {
  if (isDemo) return demo.demoInvoice(id)
  const invoice = unwrap(
    await supabase.from('admin_invoices').select('*').eq('id', id).single(),
    'Loading invoice',
  )
  const items = unwrap(
    await supabase.from('invoice_items').select('*').eq('invoice_id', id).order('sort_order'),
    'Loading invoice lines',
  )
  return { ...invoice, items: items ?? [] }
}

/**
 * Create or update a document and its lines together.
 *
 * The header is written first because a new document has no id until it comes
 * back, and replace_invoice_items() needs one. Both calls are small and the
 * second failing leaves a saved header with the previous lines — visible and
 * fixable, rather than silent.
 */
export async function saveInvoice(id, patch, items) {
  if (isDemo) throw new DemoWriteError('Saving an invoice')

  const clean = { ...patch }
  for (const key of DERIVED) delete clean[key]
  // Columns that only exist on the view, plus the nested lines.
  delete clean.items
  delete clean.supplier_name
  delete clean.balance_due
  delete clean.is_overdue
  delete clean.days_to_due

  if (!String(clean.bill_to_name ?? '').trim()) {
    throw new Error('The document needs someone to be made out to.')
  }

  let invoiceId = id
  if (id) {
    unwrap(await supabase.from('invoices').update(clean).eq('id', id).select('id').single(), 'Saving invoice')
  } else {
    const row = unwrap(
      await supabase.from('invoices').insert(clean).select('id, number').single(),
      'Creating invoice',
    )
    invoiceId = row.id
  }

  if (items) {
    unwrap(
      await supabase.rpc('replace_invoice_items', {
        p_invoice_id: invoiceId,
        p_items: items.map((item, index) => ({
          product_id: item.product_id ?? null,
          sort_order: index,
          description: item.description ?? '',
          sku: item.sku ?? '',
          detail: item.detail ?? '',
          quantity: String(item.quantity ?? 1),
          unit: item.unit ?? '',
          unit_price: String(item.unit_price ?? 0),
        })),
      }),
      'Saving invoice lines',
    )
  }

  // Re-read rather than trusting what was sent. The totals were computed by the
  // database during that write and this is the first chance to see them.
  const saved = await getInvoice(invoiceId)
  await logActivity(
    id ? 'invoice.updated' : 'invoice.created',
    'invoice',
    invoiceId,
    `${id ? 'Updated' : 'Raised'} ${saved.number} for ${saved.bill_to_name}`,
  )
  return saved
}

export async function setInvoiceStatus(id, status) {
  if (isDemo) throw new DemoWriteError('Changing the status')
  return unwrap(
    await supabase.rpc('set_invoice_status', { p_invoice_id: id, p_status: status }),
    'Updating the invoice',
  )
}

export async function recordInvoicePayment(id, amountPaid) {
  if (isDemo) throw new DemoWriteError('Recording a payment')
  return unwrap(
    await supabase.from('invoices').update({ amount_paid: amountPaid }).eq('id', id).select().single(),
    'Recording the payment',
  )
}

export async function duplicateInvoice(id, kind = null) {
  if (isDemo) throw new DemoWriteError('Copying an invoice')
  return unwrap(
    await supabase.rpc('duplicate_invoice', { p_invoice_id: id, p_kind: kind }),
    'Copying the invoice',
  )
}

/** Raise an invoice from an order. Idempotent — see the SQL. */
export async function invoiceFromOrder(orderId) {
  if (isDemo) throw new DemoWriteError('Invoicing an order')
  return unwrap(await supabase.rpc('invoice_from_order', { p_order_id: orderId }), 'Invoicing the order')
}

/** Whether this order already has an invoice, so the button can say which. */
export async function getOrderInvoice(orderId) {
  if (isDemo) return null
  const { data } = await supabase
    .from('invoices')
    .select('id, number, status, total')
    .eq('order_id', orderId)
    .eq('kind', 'invoice')
    .neq('status', 'cancelled')
    .order('created_at')
    .limit(1)
  return data?.[0] ?? null
}

export async function deleteInvoice(id) {
  if (isDemo) throw new DemoWriteError('Deleting an invoice')
  const { data: invoice } = await supabase.from('invoices').select('number').eq('id', id).maybeSingle()
  unwrapDeleted(await supabase.from('invoices').delete().eq('id', id).select('id'), 'Deleting invoice')
  await logActivity('invoice.deleted', 'invoice', id, `Deleted ${invoice?.number ?? 'an invoice'}`)
}
